import InputWrapper from "../experiences/components/InputWrapper";
import { Link, useLocation } from "react-router-dom";
import GoBack from "../experiences/components/GoBack";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import Repository from "../../repository";
import { useContext, useState } from "react";
import Meta from "../components/Meta";
import ReCaptcha from "../components/ReCaptcha";
import PasswordRequirements from "../components/PasswordRequirements";
import AppContext from "../../context";
import { createAccountSchema } from '../../validation/schemas';

const CreateAccount = () => {
    const context = useContext(AppContext);
    const location = useLocation();
    const referer = location.search.split("?referer=")[1];

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState(null);

    const { register, handleSubmit, formState: { errors }, control } = useForm({
        resolver: yupResolver(createAccountSchema),
        defaultValues: {
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            subscribe: false
        }
    });

    const email = useWatch({ name: "email", control });
    const password = useWatch({ name: "password", control });

    const submit = async (data) => {
        if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true' && !recaptchaToken) {
            setErrorMessage("Please complete the reCAPTCHA verification.");
            setError(true);
            return;
        }

        setLoading(true);

        try {
            await Repository.createAccount({ ...data, recaptchaToken });
            const result = await Repository.login(email, data.password);
            localStorage.setItem("token", result.data.token);
            window.location.href = referer ? referer : '/';
        } catch (err) {
            setErrorMessage(err?.response?.data?.message || '');
            setRecaptchaToken(null);
            setLoading(false);
            setError(true);
        }
    };

    return (
        <>
            <Meta title={'Create Account'} />
            <GoBack to={"/login"} label={"Back to Login"} />
            <form className={"w-96 mx-auto py-20 px-4 grid grid-cols-1 gap-4 rounded-lg"} onSubmit={handleSubmit(submit)}>
                <h1>Create Account</h1>
                <InputWrapper label={"Email Address"} input={<input type={"email"} {...register("email")} />} error={errors.email?.message} />
                <InputWrapper label={"Password"} input={
                    <>
                        <input type={"password"} {...register("password")} />
                        <PasswordRequirements password={password} />
                    </>
                } error={errors.password?.message} />
                <InputWrapper label={"First Name"} input={<input type={"text"} {...register("firstName")} />} error={errors.firstName?.message} />
                <InputWrapper label={"Last Name"} input={<input type={"text"} {...register("lastName")} />} error={errors.lastName?.message} />
                <InputWrapper label={"Phone Number"} input={<input type={"text"} {...register("phoneNumber")} />} error={errors.phoneNumber?.message} />
                <label className={"flex items-center gap-2 text-sm"}>
                    <input type={"checkbox"} {...register("subscribe")} />
                    Receive news and updates about {context.domain.name}.
                </label>
                <ReCaptcha
                    onChange={(token) => { setRecaptchaToken(token); if (token) { setError(false); setErrorMessage(''); } }}
                    onExpired={() => setRecaptchaToken(null)}
                />
                <button type={"submit"} disabled={loading}
                    className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md mt-2 border-0"}>
                    {loading ? '...' : 'Create Account'}
                </button>
                {error && <p className={"text-red-500"}>{errorMessage || 'There was an error creating your account, please try again.'}</p>}
                <Link className={"text-sm text-textGrey"} to={`/login${location.search}`}>Already have an account? Log In</Link>
            </form>
        </>
    );
};

export default CreateAccount;
